import { Breadcrumb, Button, Col, Collapse, Form, Input, Row, Switch, message} from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { fetchUserGroupById, updateUserGroup } from '../../slices/user-group';
import { fetchAllPermissionList, resetPermissionList } from '../../slices/user-permission';

const { Panel } = Collapse;
const toCapitalCase = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};


function EditUserGroup() {
    const [form] = Form.useForm();
    let navigate = useNavigate();
    const { id } = useParams();
    const [btnLoading, setBtnLoading] = useState(false);
    const [permissionError, setPermissionError] = useState(false);
    const dispatch = useDispatch();
    const { page,pagesize } = useSelector((state) => state.userPermission);
    const [permissionsData, setPermissionsData] = useState([]);

    useEffect(() => {
        const fetchGroup = async () => {
            try {
                dispatch(resetPermissionList());
                const [permissionList, groupResponse] = await Promise.all([
                    dispatch(fetchAllPermissionList({ page, pagesize })).unwrap(),
                    dispatch(fetchUserGroupById(id)).unwrap()
                ]);
                if (groupResponse) {
                    form.setFieldsValue({ name: groupResponse.name });
                    const existingPermissions = groupResponse.permissions || [];
                    const allPermissions = permissionList.data || [];
                    setPermissionsData(
                        allPermissions.map((permission) => ({
                            ...permission,
                            is_checked: existingPermissions.some((exist) => exist.id === permission.id),
                        }))
                    );
                }


            } catch (error) {
                console.error("");
            }
        };

        fetchGroup();
    }, [id]);

    const handleSelectAllChange = useCallback((checked, modelName) => {
        setPermissionsData((prevData) =>
            prevData.map((permission) =>
                permission.panel === modelName
                    ? { ...permission, is_checked: checked }
                    : permission
            )
        );
    }, []);
    
    const handleSwitchChange = useCallback((checked, permissionId) => {
        setPermissionsData((prevData) =>
            prevData.map((permission) =>
                permission.id === permissionId
                    ? { ...permission, is_checked: checked }
                    : permission
            )
        );
    }, []);

    const groupPermissionsByModel = (permissions) => {
        return permissions.reduce((acc, permission) => {
            if (!acc[permission.panel]) {
                acc[permission.panel] = [];
            }
            acc[permission.panel].push(permission);
            return acc;
        }, {});
    };

    const uniquePermissions = useMemo(() => groupPermissionsByModel(permissionsData), [permissionsData]);

    const handleFinish = (values) => {
        const checkedPermissions = permissionsData
            .filter((permission) => permission.is_checked) // Filter the checked permissions
            .map((permission) => permission.id);
        if (checkedPermissions.length === 0) {
            setPermissionError(true);
            return false;
        } else {
            setBtnLoading(true);
            values.permissions_id = checkedPermissions;
            dispatch(updateUserGroup({ id: id, values }))
                .unwrap()
                .then((result) => {
                    if (result.success) {
                        setBtnLoading(false);
                        message.success('User Group Updated Successfully!');
                        navigate('/manage-user-group');
                    }
                })
                .catch((error) => {
                    setBtnLoading(false);
                    message.error(error);
                });
        }

    };

    return (
        <>
            <div className='sticyaction'>
                <Row className="pagenamerow mb-0" justify="space-between" align="middle">
                    <Col>
                        <h2 style={{ margin: 0 }}>Edit Group</h2>
                        <div className="bredcrumbwrp">
                            <Link to="/manage-user-group" className="back">
                                <Back /> BACK
                            </Link>
                            <Breadcrumb
                                items={[
                                    { title: <Link to="/dashboard">Home</Link> },
                                    { title: <Link to="/manage-user-group">Groups</Link> },
                                    { title: 'Edit Group' },
                                ]}
                            />
                        </div>
                    </Col>
                </Row>
            </div>
            <div className='formwrapper'>
            <Form layout="vertical" form={form} onFinish={handleFinish} autoComplete="off">
                <div className="formsection">
                    <Row gutter={16}>
                        <Col xs={24} sm={12} xl={6}>
                            <Form.Item
                                label="Name"
                                name="name"
                                rules={[
                                    { required: true, message: 'Please enter group name' },
                                    {
                                        pattern: /^[A-Za-z\s]+$/,
                                        message: 'Only alphabets and spaces are allowed',
                                    }
                                ]}
                            >
                                <Input placeholder="Enter Group Name" onChange={(e) => {
                                    const value = e.target.value;
                                    const formattedValue = value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
                                    form.setFieldsValue({ name: formattedValue }); // Update Form state
                                }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24} >
                            {permissionError && <p style={{ color: "red" }}>Please Select Permission</p>}
                            <h3 className='adrstitle'>Permissions</h3>
                            <div style={{ padding: "0", width: "100%" }}>
                                <Collapse defaultActiveKey={[""]} style={{ width: "100%" }}>
                                    {Object.entries(uniquePermissions).map(([modelName, permissionData], index) => {
                                        const selectAll = permissionData.every((permission) => permission.is_checked);
                                        return (
                                            <Panel key={index}
                                                header={
                                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                                        {toCapitalCase(modelName)}
                                                        <div style={{ display: "flex", alignItems: "center", marginRight: "30px" }}>
                                                            <div onClick={(e) => e.stopPropagation()}>
                                                                <Switch checked={selectAll} onChange={(checked) => handleSelectAllChange(checked, modelName)} />
                                                            </div>
                                                            <span style={{ marginLeft: 10, fontWeight: "600" }}>
                                                                Select All
                                                            </span>
                                                        </div>
                                                    </div>
                                                }
                                            >
                                                {permissionData.map((permission) => {
                                                    return (
                                                        <div key={permission.id} className="permissionlist TextFeild">
                                                            <Form.Item className="radioitem">
                                                                <div onClick={(e) => e.stopPropagation()}>
                                                                    <Switch checked={permission.is_checked} onChange={(checked) => handleSwitchChange(checked, permission.id)} />
                                                                </div>
                                                            </Form.Item>
                                                            {permission.name}
                                                        </div>
                                                    );
                                                })}
                                            </Panel>
                                        );
                                    })}
                                </Collapse>
                            </div>
                        </Col>
                    </Row>
                </div>
                <div className="flex justify-end">
                    <Form.Item className="mb-0">
                        <Button type="primary" htmlType="submit" loading={btnLoading}>
                            Update Group
                        </Button>
                    </Form.Item>
                </div>
            </Form>
            </div>
        </>
    );
}

export default EditUserGroup;
